import jwt from 'jsonwebtoken';
import {
  AUTHORIZED_DOMAINS,
} from 'containers/Authentication/constants';

let CHECK_JWT_REFRESH_TIMER_KEY: any = 0;
let googleIdentityScriptPromise: Promise<any> | null = null;

const GOOGLE_IDENTITY_SCRIPT_URL = 'https://accounts.google.com/gsi/client';
const GOOGLE_BUTTON_WIDTH = 260;
export const GOOGLE_ID_TOKEN_ISSUER = 'https://accounts.google.com';

type GoogleCredentialResponse = {
  credential?: string;
  select_by?: string;
};

type RenderGoogleSignInButtonArgs = {
  element: HTMLElement;
  clientId: string;
  onCredential: (response: GoogleCredentialResponse) => void;
  onError: (error: any) => void;
};

export const isValidEmail = email =>
  /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email);

export const isAuthorizedDomain = (email: string): boolean => {
  const ind = email.lastIndexOf('@');
  const domain = ind !== -1 && email.slice(ind + 1);
  if (domain && AUTHORIZED_DOMAINS.includes(domain)) {
    return true;
  }
  return false;
};

export const getJWTExpirationDate = token => {
  if (!token) return null;

  const res = jwt.decode(token);
  const decoded = typeof res === 'string' ? null : res;
  const date = new Date(0);

  if (!(decoded && decoded.exp)) {
    return null;
  }

  date.setUTCSeconds(decoded.exp);

  return date;
};

export const getJWTIssuer = token => {
  if (!token) return null;

  const res = jwt.decode(token);
  const decoded = typeof res === 'string' ? null : res;

  return decoded?.iss || null;
};

export const stopJWTRefreshChecker = () => {
  clearInterval(CHECK_JWT_REFRESH_TIMER_KEY);
  CHECK_JWT_REFRESH_TIMER_KEY = 0;
};

export const isJWTExpired = token => {
  const date = getJWTExpirationDate(token);
  if (date === null) {
    return false;
  }
  // subtract jwt expiration time by 10 mins
  return !(date.valueOf() - 10 * 60 * 1000 > new Date().valueOf());
};

const loadGoogleIdentityServices = () => {
  if (window.google?.accounts?.id) {
    return Promise.resolve(window.google);
  }

  if (googleIdentityScriptPromise) {
    return googleIdentityScriptPromise;
  }

  googleIdentityScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(
      `script[src="${GOOGLE_IDENTITY_SCRIPT_URL}"]`,
    ) as HTMLScriptElement | null;

    // Fix: reset promise on rejection too
    const handleLoad = () => {
      if (window.google?.accounts?.id) resolve(window.google);
      else {
        googleIdentityScriptPromise = null;  // ← add this
        reject(new Error('Google Identity Services did not load correctly'));
      }
    };

    const handleError = () => {
      googleIdentityScriptPromise = null;
      reject(new Error('Unable to load Google Identity Services'));
    };

    if (existingScript) {
      existingScript.addEventListener('load', handleLoad, { once: true });
      existingScript.addEventListener('error', handleError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = GOOGLE_IDENTITY_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = handleLoad;
    script.onerror = handleError;
    document.head.appendChild(script);
  });

  return googleIdentityScriptPromise;
};

export const renderGoogleSignInButton = async ({
  element,
  clientId,
  onCredential,
  onError,
}: RenderGoogleSignInButtonArgs) => {
  try {
    const google = await loadGoogleIdentityServices();
    google.accounts.id.initialize({
      client_id: clientId,
      callback: response => {
        if (!response?.credential) {
          onError({ error: 'missing_google_credential' });
          return;
        }

        onCredential(response);
      },
    });
    element.innerHTML = '';
    google.accounts.id.renderButton(element, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
      logo_alignment: 'left',
      width: GOOGLE_BUTTON_WIDTH,
    });
  } catch (error) {
    onError(error);
  }
};

export const buildGoogleProfileFromIdToken = token => {
  const res = jwt.decode(token);
  const decoded = typeof res === 'string' ? null : res;

  if (decoded?.iss !== GOOGLE_ID_TOKEN_ISSUER || !decoded?.email) {
    throw new Error('Unable to read Google account details');
  }

  return {
    email: decoded.email,
    familyName: decoded.family_name,
    givenName: decoded.given_name,
    googleId: decoded.sub,
    imageUrl: decoded.picture,
    name: decoded.name,
  };
};

export const disableGoogleAutoSelect = () => {
  try {
    window.google?.accounts?.id?.disableAutoSelect?.();
  } catch {
    // Never block logout if GIS state is unavailable.
  }
};

const checkJWTExpiry = ({ loggedIn, logoutUser }) => {
  const token = localStorage.getItem('authorization-token');
  if (isJWTExpired(token) && loggedIn) {
    logoutUser();
  }
};

// start expiry check once every second — logs user out when Google ID token expires (~50 min)
export const startJWTRefreshChecker = ({
  loggedIn,
  logoutUser,
  refreshSession: _refreshSession,
}) => {
  if (CHECK_JWT_REFRESH_TIMER_KEY) return;

  CHECK_JWT_REFRESH_TIMER_KEY = setInterval(() => {
    checkJWTExpiry({ loggedIn, logoutUser });
  }, 1000);
};
